/**
 * Schedule callback when main thread is idle.
 * Falls back to a short timeout when requestIdleCallback is unavailable.
 * @param {() => void} callback
 * @param {{ timeout?: number }} [options]
 * @returns {() => void} Canceler
 */
function scheduleIdle(callback, options = {}) {
  const timeout = options.timeout ?? 2000;
  if (typeof requestIdleCallback !== 'undefined') {
    const id = requestIdleCallback(callback, { timeout });
    return () => cancelIdleCallback(id);
  }

  const id = setTimeout(callback, 1);
  return () => clearTimeout(id);
}

/**
 * Shared JSON/fetch config helper.
 * @param {string} [type='json']
 * @returns {{ method: string, headers: { 'Content-Type': string, Accept: string } }}
 */
function fetchConfig(type = 'json') {
  return {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Accept: `application/${type}`,
    },
  };
}


(function () {
/**
 * product-card – Product card wrapper with lazy blur removal and hover image loading
 * Handles js-lazyloadBlur placeholder fade and data-hover-image-placeholder intersection load.
 */
if (!customElements.get("product-card")) {
  class ProductCard extends HTMLElement {
    #intersectionObserver = null;

    connectedCallback() {
      const image = this.querySelector("img.js-lazyloadBlur");
      const lazyloadPlaceholder = this.querySelector(".js-lazyloadBlurPlaceholder");
      if (lazyloadPlaceholder && image) {
        if (image.complete) {
          lazyloadPlaceholder.style.opacity = 0;
          setTimeout(() => lazyloadPlaceholder.remove(), 100);
        } else {
          image.addEventListener("load", () => {
            lazyloadPlaceholder.style.opacity = 0;
            setTimeout(() => lazyloadPlaceholder.remove(), 100);
          });
        }
      }
      this.#setupHoverImage();
    }

    disconnectedCallback() {
      this.#intersectionObserver?.disconnect();
      this.#intersectionObserver = null;
    }

    /**
     * Load hover image when card enters (or nears) viewport so it's ready before hover.
     * No-op on touch devices (no hover).
     * @private
     */
    #setupHoverImage() {
      if ("ontouchstart" in window || navigator.maxTouchPoints > 0) return;
      const placeholder = this.querySelector("[data-hover-image-placeholder]");
      if (!placeholder?.getAttribute("data-hover-src")) return;
      this.#intersectionObserver?.disconnect();
      this.#intersectionObserver = new IntersectionObserver(
        (entries) => {
          const entry = entries[0];
          if (!entry?.isIntersecting) return;
          this.#intersectionObserver?.disconnect();
          this.#intersectionObserver = null;
          if (
            !placeholder.querySelector("img") &&
            !placeholder.hasAttribute("data-hover-loading")
          ) {
            this.#injectHoverImage(placeholder);
          }
        },
        {
          rootMargin: "100px",
          threshold: 0,
        }
      );
      this.#intersectionObserver.observe(this);
    }

    /**
     * Fetch hover image and inject img into placeholder once loaded.
     * @param {HTMLElement} placeholder
     * @private
     */
    #injectHoverImage(placeholder) {
      if (!placeholder) return;
      const src = placeholder.getAttribute("data-hover-src");
      if (!src) return;
      placeholder.setAttribute("data-hover-loading", "");
      const alt = placeholder.getAttribute("data-hover-alt") ?? "";
      const objectFit = placeholder.getAttribute("data-hover-object-fit") ?? "object-contain";
      const preload = new Image();
      preload.onload = () => {
        placeholder.removeAttribute("data-hover-loading");
        const img = document.createElement("img");
        img.src = src;
        img.alt = alt;
        img.loading = "lazy";
        img.decoding = "async";
        img.className = `mix-blend-darken w-full h-full ${objectFit} aspect-[0.8]`;
        placeholder.appendChild(img);
      };
      preload.onerror = () => {
        placeholder.removeAttribute("data-hover-loading");
      };
      preload.src = src;
    }
  }

  customElements.define("product-card", ProductCard);
}
})();
(function () {
/**
 * Product Card Swatches Web Component
 * Handles image preloading on hover and image switching on click
 * Optimized for performance with lazy loading and memory management
 */
if (!customElements.get("product-card-swatches")) {
  class ProductCardSwatches extends HTMLElement {
    constructor() {
      super();
      this.preloadedImages = new Map();
      this.currentHoverTarget = null;
      this.hoverTimeout = null;
      this.intersectionObserver = null;
      this.isIntersecting = false;
    }

    connectedCallback() {
      this.setupEventListeners();
      this.setupIntersectionObserver();
    }

    disconnectedCallback() {
      this.cleanup();
    }

    setupEventListeners() {
      // Use event delegation for better performance
      this.addEventListener("mouseover", this.handleMouseOver.bind(this));
      this.addEventListener("mouseout", this.handleMouseOut.bind(this));
      this.addEventListener("change", this.handleChange.bind(this));

      // Touch events for mobile
      this.addEventListener("touchstart", this.handleTouchStart.bind(this));
    }

    setupIntersectionObserver() {
      // Only preload images when component is visible
      this.intersectionObserver = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            this.isIntersecting = entry.isIntersecting;
          });
        },
        { threshold: 0.1 },
      );
      this.intersectionObserver.observe(this);
    }

    handleMouseOver(event) {
      const label = event.target.closest("label");
      if (!label || !this.isIntersecting) return;

      this.currentHoverTarget = label;

      // Clear existing timeout
      if (this.hoverTimeout) {
        clearTimeout(this.hoverTimeout);
      }

      // Start preloading after a short delay to avoid unnecessary requests
      this.hoverTimeout = setTimeout(() => {
        this.preloadImage(label);
      }, 150);
    }

    handleMouseOut(event) {
      // Clear timeout if mouse leaves before preloading starts
      if (this.hoverTimeout) {
        clearTimeout(this.hoverTimeout);
        this.hoverTimeout = null;
      }
      this.currentHoverTarget = null;
    }

    handleChange(event) {
      const radio = event.target;
      if (!radio || radio.type !== "radio") return;

      this.switchProductImage(radio);
    }

    handleTouchStart(event) {
      const button = event.target.closest("button");
      if (!button) return;

      // For touch devices, preload immediately on touch
      this.preloadImage(button);
    }

    async preloadImage(label) {
      const radio = label.querySelector('input[type="radio"]');
      if (!radio) return;

      const imageUrl = radio.dataset.image;
      const hoverImageUrl = radio.dataset.hoverImage;

      if (!imageUrl || this.preloadedImages.has(imageUrl)) return;

      try {
        // Preload both main and hover images
        const promises = [this.loadImage(imageUrl)];
        if (hoverImageUrl && hoverImageUrl !== imageUrl) {
          promises.push(this.loadImage(hoverImageUrl));
        }

        await Promise.all(promises);
        this.preloadedImages.set(imageUrl, true);
      } catch (error) {
        // Preload image failed
      }
    }

    loadImage(url) {
      return new Promise((resolve, reject) => {
        const img = new Image();
        img.onload = () => resolve(img);
        img.onerror = reject;
        img.src = url;
      });
    }

    switchProductImage(radio) {
      const productCard = this.closest("product-card");
      if (!productCard) return;

      const imageUrl = radio.dataset.image;
      const hoverImageUrl = radio.dataset.hoverImage;
      const productUrl = radio.dataset.url;
      const shouldNavigate = this.dataset.navigate === "true";

      if (!imageUrl) return;

      // Update main image
      const mainImage = productCard.querySelector("picture:first-of-type img");
      if (mainImage) {
        this.updateImageSrc(mainImage, imageUrl);
      }

      // Update hover image
      const hoverImage = productCard.querySelector("picture:nth-of-type(2) img");
      if (hoverImage && hoverImageUrl) {
        this.updateImageSrc(hoverImage, hoverImageUrl);
      }

      // Update URLs of all anchor elements in the product card
      if (productUrl) {
        this.updateProductUrls(productCard, productUrl);
      }

      // Update active state on labels
      this.updateActiveState(radio);

      // Update product price to match selected color/variant
      this.updateProductPrice(productCard, radio);

      // Update wishlist button with new variant ID
      this.updateWishlistButton(productCard, productUrl);

      // Navigate to product URL only if explicitly enabled
      if (productUrl && shouldNavigate) {
        // Use a small delay to allow image update to be visible
        setTimeout(() => {
          window.location.href = productUrl;
        }, 100);
      }
    }

    updateImageSrc(imgElement, newSrc) {
      // Use a more efficient approach for image switching
      if (imgElement.src !== newSrc) {
        // Remove srcset to prevent conflicts with image switching
        if (imgElement.hasAttribute("srcset")) {
          imgElement.removeAttribute("srcset");
        }

        // remove sources to prevent conflicts with image switching
        imgElement
          .closest("picture")
          .querySelectorAll("source")
          .forEach((source) => {
            source.remove();
          });

        // Create a new image element for smooth transition
        const newImg = new Image();
        newImg.onload = () => {
          imgElement.src = newSrc;
          imgElement.classList.add("image-updated");
          // Remove class after transition
          setTimeout(() => {
            imgElement.classList.remove("image-updated");
          }, 300);
        };
        newImg.src = newSrc;
      }
    }

    updateProductUrls(productCard, newUrl) {
      // Update all anchor elements within the product card
      const anchorElements = productCard.querySelectorAll("a[href]");
      anchorElements.forEach((anchor) => {
        // Only update if the anchor doesn't have a specific data attribute to preserve
        if (!anchor.hasAttribute("data-preserve-url")) {
          anchor.href = newUrl;
        }
      });
    }

    updateProductPrice(productCard, radio) {
      if (!productCard || !radio) return;

      const priceContainer = productCard.querySelector("[data-product-card-price]");
      const nextPriceHtml = radio.dataset.priceHtml;
      if (!priceContainer || !nextPriceHtml) return;

      const decodedHtml = this.decodeHtmlEntities(nextPriceHtml).trim();
      if (!decodedHtml) return;

      priceContainer.innerHTML = decodedHtml;
      productCard.dataset.price = radio.dataset.variantPrice || productCard.dataset.price;
    }

    decodeHtmlEntities(encodedString) {
      if (!encodedString) return "";

      const textarea = document.createElement("textarea");
      textarea.innerHTML = encodedString;
      return textarea.value;
    }

    updateActiveState(activeRadio) {
      // Remove active state from all labels
      this.querySelectorAll("label").forEach((label) => {
        label.classList.remove("active");
        label.classList.remove("is-active");
        const span = label.querySelector("span");
        if (span) {
          span.style.setProperty("--active-indicator", "none");
        }
      });

      // Add active state to selected radio's label
      const activeLabel = activeRadio.closest("label");
      if (activeLabel) {
        activeLabel.classList.add("active");
        activeLabel.classList.add("is-active");
        const span = activeLabel.querySelector("span");
        if (span) {
          span.style.setProperty("--active-indicator", "block");
        }
      }
    }

    /**
     * Update wishlist button with new variant ID when swatch changes
     * @param {HTMLElement} productCard - The product card element
     * @param {string} productUrl - The new product URL (may contain variant query param)
     */
    updateWishlistButton(productCard, productUrl) {
      if (!productCard) return;

      try {
        // Find the wishlist button in the product card
        const wishButton = productCard.querySelector('electric-wish');
        if (!wishButton) return;

        // Try to get variant ID from the radio button's data attribute first
        // This is more reliable than parsing the URL
        const activeRadio = this.querySelector('input[type="radio"]:checked');
        let variantId = activeRadio?.dataset.variantId;

        // Fallback to extracting from URL if data-variant-id is not available
        if (!variantId && productUrl) {
          try {
            const url = new URL(productUrl, window.location.origin);
            variantId = url.searchParams.get('variant');
          } catch (e) {
            // URL parsing failed, skip
          }
        }

        if (!variantId) {
          // No variant ID available, can't update wishlist button
          return;
        }

        // Update the variant ID on the wishlist button
        const oldVariantId = wishButton.dataset.targetVariantId || wishButton.dataset.targetVariantid;
        wishButton.dataset.targetVariantId = variantId;

        // Only sync if variant actually changed
        if (oldVariantId !== variantId) {
          // Trigger sync to update the wishlist state for the new variant
          // Dispatch a custom event that electric-wish can listen to
          wishButton.dispatchEvent(new CustomEvent('wishlist:variant-changed', {
            detail: { variantId, oldVariantId },
            bubbles: false
          }));
        }
      } catch (error) {
        // Failed to update wishlist button
      }
    }

    cleanup() {
      if (this.hoverTimeout) {
        clearTimeout(this.hoverTimeout);
      }
      if (this.intersectionObserver) {
        this.intersectionObserver.disconnect();
      }
      this.preloadedImages.clear();
    }
  }

  customElements.define("product-card-swatches", ProductCardSwatches);
}
})();
(function () {
/**
 * Product Card Add to Cart Component
 * 
 * Handles add-to-cart functionality for product cards with add-to-bag icons.
 * Provides loading states, error handling, and cart updates.
 * 
 * @example
 * <button data-add-to-cart-trigger data-product-id="123" data-variant-id="456">
 *   <icon>add-to-bag</icon>
 * </button>
 */
class ProductCardATC extends HTMLElement {
  /** @type {HTMLButtonElement | null} Add to cart trigger button */
  #trigger = null;
  
  /** @type {HTMLFormElement | null} Hidden product form */
  #form = null;
  
  /** @type {boolean} Component connection state */
  #isConnected = false;
  
  /** @type {AbortController | null} Event listener controller */
  #abortController = null;

  constructor() {
    super();
  }

  connectedCallback() {
    if (this.#isConnected) return;
    
    try {
      this.#isConnected = true;
      this.#initialize();
    } catch (error) {
      this.#handleError('Failed to initialize product card ATC', error);
    }
  }

  disconnectedCallback() {
    this.#cleanup();
  }

  /**
   * Initialize the component
   * @private
   */
  #initialize() {
    this.#trigger = this.querySelector('[data-add-to-cart-trigger]');
    this.#form = this.querySelector('form[data-type="add-to-cart-form"]');
    
    if (!this.#trigger || !this.#form) {
      this.#logError(new Error('Missing required elements: trigger button or form'));
      return;
    }

    this.#setupEventListeners();
  }

  /**
   * Setup event listeners for add to cart functionality
   * @private
   */
  #setupEventListeners() {
    this.#abortController = new AbortController();
    
    this.#trigger.addEventListener('click', this.#handleAddToCart.bind(this), {
      signal: this.#abortController.signal,
      passive: false
    });
    
    this.#trigger.addEventListener('keydown', this.#handleKeydown.bind(this), {
      signal: this.#abortController.signal,
      passive: false
    });
  }

  /**
   * Handle add to cart button click.
   * On cart page: always AJAX (fetch + section update) to avoid full reload.
   * Desktop (lg+): native form submit → redirect to cart page.
   * Mobile/tablet: fetch then cart:added → cart drawer opens.
   * @param {Event} event - Click event
   * @private
   */
  async #handleAddToCart(event) {
    event.preventDefault();
    if (this.#trigger.disabled) return;

    const isCartPage = typeof window !== 'undefined' && window.location?.pathname?.replace(/\/$/, '') === '/cart';
    const bp = typeof getCurrentBreakpoint === 'function' ? getCurrentBreakpoint() : '';
    const useRedirect = !isCartPage && ['lg', 'xl', 'wide'].includes(bp);

    if (useRedirect) {
      this.#setLoadingState(true);
      if (typeof sessionStorage !== 'undefined') {
        sessionStorage.setItem('cart:justAdded', '1');
      }
      const form = this.#form;
      setTimeout(() => {
        if (form?.parentNode) form.submit();
      }, 120);
      return;
    }

    try {
      this.#setLoadingState(true);
      // Open cart drawer immediately (before fetch) so UX feels instant
      document.dispatchEvent(new CustomEvent('cart:adding', { bubbles: true }));

      const formData = new FormData(this.#form);
      
      // Get dynamic sections for cart updates
      const dynamicSectionsNodes = document.querySelectorAll('cart-dynamic');
      const sectionIds = dynamicSectionsNodes.length
        ? [...dynamicSectionsNodes].map(n => n.dataset.sectionId)
        : [];
      
      formData.append('sections', sectionIds.join(','));
      
      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: {
          'X-Requested-With': 'XMLHttpRequest'
        },
        body: formData
      });
      
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }
      
      const result = await response.json();
      
      if (result.status) {
        throw new Error(result.description || 'Failed to add to cart');
      }
      
      this.#handleSuccess(result);
      
    } catch (error) {
      this.#handleError('Failed to add product to cart', error);
    } finally {
      this.#setLoadingState(false);
    }
  }

  /**
   * Handle keyboard navigation
   * @param {KeyboardEvent} event - Keydown event
   * @private
   */
  #handleKeydown(event) {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      this.#trigger.click();
    }
  }

  /**
   * Set loading state for the button
   * @param {boolean} isLoading - Whether to show loading state
   * @private
   */
  #setLoadingState(isLoading) {
    if (isLoading) {
      this.#trigger.setAttribute('data-state', 'loading');
      this.#trigger.setAttribute('aria-label', 'Adding to cart...');
    } else {
      this.#trigger.removeAttribute('data-state');
      this.#trigger.setAttribute('aria-label', 'Add to cart');
    }
  }

  /**
   * Handle successful add to cart
   * @param {Object} result - Cart add result
   * @private
   */
  #handleSuccess(result) {
    // Set success state on button
    this.#trigger.setAttribute('data-state', 'success');
    
    // Update dynamic sections if provided
    const dynamicSectionsNodes = document.querySelectorAll('cart-dynamic');
    if (dynamicSectionsNodes.length && result.sections) {
      [...dynamicSectionsNodes].forEach(node => {
        const htmlString = result.sections[node.dataset.sectionId];
        if (!htmlString) return;
        
        const html = new DOMParser().parseFromString(htmlString, 'text/html');
        const updated = html.querySelector(`cart-dynamic[data-section-id='${node.dataset.sectionId}']`);
        if (!updated) return;
        
        node.innerHTML = updated.innerHTML;
        node.className = updated.className;
        if (typeof window.unveilLazyloadIn === 'function') {
          window.unveilLazyloadIn(node);
        }
        // Call lifecycle hook if present
        if (typeof node.modified === 'function') {
          node.modified();
        }
      });
    }
    
    document.dispatchEvent(new CustomEvent('cart:update', {
      detail: {
        items: [result],
        source: 'product-card-atc',
        pageAlreadyRefreshed: true
      },
      bubbles: true
    }));

    document.dispatchEvent(new CustomEvent('cart:added', {
      detail: {
        items: [result],
        source: 'product-card-atc'
      },
      bubbles: true
    }));

    if (this.closest('electric-rebuy-simplify') && typeof window.rebuyTrackEvent === 'function' && result.product_id != null) {
      window.rebuyTrackEvent('added', {
        product_id: result.product_id,
        variant_id: result.variant_id,
        product_handle: result.handle || undefined
      });
      if (typeof window.rebuyMarkAddedVariant === 'function') window.rebuyMarkAddedVariant(result.variant_id);
    }

    // Haptic feedback if available
    navigator.vibrate?.(100);
    
    // Remove success state after delay
    setTimeout(() => {
      this.#trigger.removeAttribute('data-state');
    }, 3000);
  }

  /**
   * Show notification in top-right corner
   * @param {string} message - Notification message
   * @private
   */
  #showNotification(message) {
    // Remove existing notification if present
    const existingNotification = document.querySelector('.product-card-notification');
    if (existingNotification) {
      existingNotification.remove();
    }
    
    // Create notification element
    const notification = document.createElement('div');
    notification.className = 'product-card-notification fixed top-4 right-4 z-[9999] bg-contrast text-contrast px-4 py-2 shadow-lg transform translate-x-full transition-transform duration-300 ease-out';
    notification.textContent = message;
    
    // Add to document
    document.body.appendChild(notification);
    
    // Animate in
    requestAnimationFrame(() => {
      notification.classList.remove('translate-x-full');
    });
    
    // Remove after delay
    setTimeout(() => {
      notification.classList.add('translate-x-full');
      setTimeout(() => {
        if (notification.parentNode) {
          notification.remove();
        }
      }, 300);
    }, 2000);
  }

  /**
   * Handle errors gracefully
   * @param {string} message - Error message
   * @param {Error} error - Original error
   * @private
   */
  #handleError(message, error) {
    // Set error state on button
    this.#trigger.setAttribute('data-state', 'error');
    
    // Haptic feedback for error
    navigator.vibrate?.(200);
    
    // Remove error state after delay
    setTimeout(() => {
      this.#trigger.removeAttribute('data-state');
    }, 3000);
  }

  /**
   * Log errors for debugging
   * @param {Error} error - Error to log
   * @private
   */
  #logError(error) {}

  /**
   * Clean up resources and event listeners
   * @private
   */
  #cleanup() {
    this.#isConnected = false;
    this.#abortController?.abort();
    this.#abortController = null;
  }
}

// Register the component
customElements.define('product-card-atc', ProductCardATC);
})();
(function () {
/**
 * Cart line property [_instock] sync
 *
 * Sets each line item's properties._instock to "true" or "false" from Liquid-computed
 * stock rules (snippets/cart--instock-data.liquid), matching product--shipping-date /
 * ships-in-weeks logic per line — not one combined cart flag.
 *
 * Reads `.cart-instock-data` JSON `lines` (key, variantId, quantity, instock). POSTs
 * /cart/change.js by line item key when the stored property differs. Never applies a
 * flag by cart index: a same-length stale snapshot must not land on another line.
 *
 * A line is updated only when its key, variant id, and quantity still match the
 * snapshot. A mismatch retries once against a fresh section render.
 *
 * Clears legacy cart.attributes._instock when present so integrations do not read a
 * single aggregate value.
 *
 * Runs on: DOMContentLoaded, cart:added, cart:update, cart checkout form submit.
 *
 * Debug: `?ii_instock_debug=1` (alias `?instockDebug=1`) — console + overlay panel
 * with per-line stored vs computed `_instock`, cart attribute, shipping note, sync status.
 * Also on when Shopify theme editor (`Shopify.designMode`) is active.
 * Disable writes: `?instockNoSync=1`
 *
 * Both require server-side `window.iiInternalPreview` (theme editor or Shopify theme
 * preview). On the published live storefront for customers, URL params are no-ops.
 *
 * @see snippets/cart--instock-data.liquid
 * @see snippets/_script-variables.liquid
 */


(function () {
  const DEBOUNCE_MS = 150;
  const MAX_SYNC_ATTEMPTS = 3;
  const DATA_SELECTOR = '.cart-instock-data';
  const PROP_KEY = '_instock';
  const LEGACY_CART_ATTR_KEY = '_instock';
  const NO_SYNC_QUERY_PARAM = 'instockNoSync';
  const DEBUG_QUERY_PARAM = 'ii_instock_debug';
  const DEBUG_QUERY_PARAM_ALIAS = 'instockDebug';
  const LOG_PREFIX = '[cart-line-instock]';
  const OVERLAY_ID = 'ii-instock-debug-overlay';
  const BADGE_ATTR = 'data-ii-instock-debug-badge';
  const CART_DRAWER_SELECTOR = 'drawer-element#cart-drawer';
  const ITEMS_SECTION_SELECTOR = 'cart-dynamic[data-section-id="api--cart--items"]';
  const CART_ITEMS_SECTION_ID = 'api--cart--items';
  const CHECKOUT_FORM_SELECTOR = 'form.cart-checkout-form';
  const LINE_KEY_SELECTOR = '[data-cart-line-key]';
  const SHIPPING_DATE_SELECTOR = '[data-cart-shipping-date]';

  let debounceTimer = null;
  let syncing = false;
  let syncQueued = false;

  /**
   * @typedef {Object} InstockLine
   * @property {string} key
   * @property {number} variantId
   * @property {number} quantity
   * @property {boolean} instock
   */

  /**
   * @typedef {Object} DebugLineRow
   * @property {string} key
   * @property {string} title
   * @property {number} variantId
   * @property {number} quantity
   * @property {'true'|'false'|null} stored
   * @property {'true'|'false'|null} computedServer
   * @property {'true'|'false'|null} computedDom
   * @property {boolean} match
   * @property {string} [note]
   */

  /** @type {{
   *   status: string,
   *   lastError: string|null,
   *   attempt: number,
   *   syncing: boolean,
   *   noSync: boolean,
   *   source: string|null,
   *   legacyAttr: string|null,
   *   cartNote: string|null,
   *   shippingNote: string|null,
   *   lines: DebugLineRow[],
   *   updatedAt: string|null,
   * }} */
  const debugState = {
    status: 'idle',
    lastError: null,
    attempt: 0,
    syncing: false,
    noSync: false,
    source: null,
    legacyAttr: null,
    cartNote: null,
    shippingNote: null,
    lines: [],
    updatedAt: null,
  };

  /**
   * Server-gated: only true when Liquid set window.iiInternalPreview
   * (theme editor or unpublished/theme-preview bar context).
   * @returns {boolean}
   */
  function isInternalPreview() {
    try {
      return typeof window !== 'undefined' && window.iiInternalPreview === true;
    } catch (_) {
      return false;
    }
  }

  function isDebugMode() {
    if (!isInternalPreview()) return false;
    const params = new URLSearchParams(window.location.search);
    if (
      params.get(DEBUG_QUERY_PARAM) === '1' ||
      params.get(DEBUG_QUERY_PARAM_ALIAS) === '1'
    ) {
      return true;
    }
    try {
      if (window.Shopify?.designMode) return true;
    } catch (_) {
      /* ignore */
    }
    return false;
  }

  function debugLog(...args) {
    if (isDebugMode()) console.log(LOG_PREFIX, ...args);
  }

  function isNoSyncMode() {
    if (!isInternalPreview()) return false;
    const params = new URLSearchParams(window.location.search);
    return params.get(NO_SYNC_QUERY_PARAM) === '1';
  }

  /**
   * @param {Partial<typeof debugState>} patch
   */
  function setDebugState(patch) {
    if (!isDebugMode()) return;
    Object.assign(debugState, patch);
    debugState.updatedAt = new Date().toISOString();
    renderDebugUi();
  }

  /**
   * @param {InstockLine[] | null | undefined} lines
   * @param {string} key
   * @returns {'true'|'false'|null}
   */
  function computedFlagForKey(lines, key) {
    if (!lines) return null;
    const row = lines.find((l) => l.key === key);
    if (!row) return null;
    return row.instock ? 'true' : 'false';
  }

  /**
   * @returns {string|null}
   */
  function readShippingNoteText() {
    const nodes = document.querySelectorAll(SHIPPING_DATE_SELECTOR);
    for (let i = 0; i < nodes.length; i++) {
      const text = (nodes[i].textContent || '').replace(/\s+/g, ' ').trim();
      if (text) return text;
    }
    return null;
  }

  /**
   * Build overlay snapshot from cart.js + Liquid snapshots (does not write).
   * @param {{ cart?: object|null, serverLines?: InstockLine[]|null, domLines?: InstockLine[]|null, source?: string|null, status?: string, attempt?: number, error?: string|null }} opts
   */
  function refreshDebugSnapshot(opts = {}) {
    if (!isDebugMode()) return;
    const cart = opts.cart || null;
    const serverLines = opts.serverLines ?? null;
    const domLines = opts.domLines ?? getComputedLinesFromDom();
    /** @type {DebugLineRow[]} */
    const lines = [];
    const items = Array.isArray(cart?.items) ? cart.items : [];

    for (let i = 0; i < items.length; i++) {
      const item = items[i];
      const key = String(item.key || '');
      const stored = instockPropStoredValue(item.properties?.[PROP_KEY]);
      const computedServer = computedFlagForKey(serverLines, key);
      const computedDom = computedFlagForKey(domLines, key);
      const expected = computedServer ?? computedDom;
      const match = expected !== null && stored === expected;
      let note = '';
      if (expected === null) note = 'no computed row';
      else if (stored === null) note = 'property missing';
      else if (!match) note = 'MISMATCH';
      lines.push({
        key,
        title: String(item.product_title || item.title || '').slice(0, 48),
        variantId: Number(item.variant_id),
        quantity: Number(item.quantity),
        stored,
        computedServer,
        computedDom,
        match,
        note,
      });
    }

    const legacyRaw =
      cart?.attributes && LEGACY_CART_ATTR_KEY in cart.attributes
        ? cart.attributes[LEGACY_CART_ATTR_KEY]
        : null;

    setDebugState({
      status: opts.status || debugState.status,
      lastError: opts.error !== undefined ? opts.error : debugState.lastError,
      attempt: opts.attempt ?? debugState.attempt,
      syncing,
      noSync: isNoSyncMode(),
      source: opts.source ?? debugState.source,
      legacyAttr:
        legacyRaw === undefined || legacyRaw === null || legacyRaw === ''
          ? null
          : String(legacyRaw),
      cartNote: cart?.note ? String(cart.note) : null,
      shippingNote: readShippingNoteText(),
      lines,
    });
    paintLineBadges(lines);
  }

  /**
   * @param {DebugLineRow[]} lines
   */
  function paintLineBadges(lines) {
    if (!isDebugMode() || typeof document === 'undefined') return;
    document.querySelectorAll(`[${BADGE_ATTR}]`).forEach((el) => el.remove());
    const byKey = new Map(lines.map((l) => [l.key, l]));
    document.querySelectorAll(LINE_KEY_SELECTOR).forEach((row) => {
      const key = row.getAttribute('data-cart-line-key');
      if (!key) return;
      const info = byKey.get(key);
      if (!info) return;
      const badge = document.createElement('div');
      badge.setAttribute(BADGE_ATTR, '1');
      badge.style.cssText =
        'margin-top:4px;font:11px/1.3 ui-monospace,SFMono-Regular,Menlo,monospace;' +
        'padding:2px 6px;border-radius:4px;display:inline-block;max-width:100%;' +
        'background:#111;color:#fff;';
      const ok = info.match;
      badge.style.background = ok ? '#0a7a32' : '#a11';
      const stored = info.stored === null ? '∅' : info.stored;
      const expected = info.computedServer ?? info.computedDom ?? '?';
      badge.textContent = `_instock ${stored} → ${expected}${ok ? '' : ' ⚠'}`;
      const titleEl =
        row.querySelector('a[href*="/products/"]')?.parentElement || row;
      titleEl.appendChild(badge);
    });
  }

  function ensureOverlayRoot() {
    let root = document.getElementById(OVERLAY_ID);
    if (root) return root;
    root = document.createElement('aside');
    root.id = OVERLAY_ID;
    root.setAttribute('aria-label', 'ii _instock debug');
    root.style.cssText =
      'position:fixed;z-index:2147483646;right:12px;bottom:12px;width:min(420px,calc(100vw - 24px));' +
      'max-height:min(70vh,560px);overflow:auto;background:#0b0b0b;color:#f2f2f2;' +
      'border:1px solid #444;border-radius:8px;box-shadow:0 8px 28px rgba(0,0,0,.45);' +
      'font:12px/1.4 ui-monospace,SFMono-Regular,Menlo,monospace;padding:10px 12px;';
    document.body.appendChild(root);
    return root;
  }

  function renderDebugUi() {
    if (!isDebugMode() || typeof document === 'undefined') return;
    if (!document.body) {
      document.addEventListener('DOMContentLoaded', renderDebugUi, { once: true });
      return;
    }
    const root = ensureOverlayRoot();
    const statusColor =
      debugState.status === 'error'
        ? '#ff6b6b'
        : debugState.syncing
          ? '#f0c14b'
          : debugState.status === 'ok'
            ? '#6bff95'
            : '#ccc';

    const lineRows = debugState.lines
      .map((l) => {
        const stored = l.stored === null ? '∅' : l.stored;
        const srv = l.computedServer === null ? '—' : l.computedServer;
        const dom = l.computedDom === null ? '—' : l.computedDom;
        const mark = l.match ? '✓' : '✗';
        const shortKey = l.key.length > 18 ? `${l.key.slice(0, 10)}…` : l.key;
        return (
          `<tr>` +
          `<td style="padding:2px 4px;vertical-align:top;">${mark}</td>` +
          `<td style="padding:2px 4px;vertical-align:top;">` +
          `<div>${escapeHtml(l.title || shortKey)}</div>` +
          `<div style="opacity:.7">v${l.variantId} ×${l.quantity}</div>` +
          `<div style="opacity:.7">key ${escapeHtml(shortKey)}</div>` +
          `</td>` +
          `<td style="padding:2px 4px;vertical-align:top;white-space:nowrap;">` +
          `stored <b>${stored}</b><br>server <b>${srv}</b><br>dom <b>${dom}</b>` +
          (l.note ? `<br><span style="color:#f0c14b">${escapeHtml(l.note)}</span>` : '') +
          `</td></tr>`
        );
      })
      .join('');

    root.innerHTML =
      `<div style="display:flex;justify-content:space-between;gap:8px;align-items:center;margin-bottom:8px;">` +
      `<strong>ii _instock debug</strong>` +
      `<span style="color:${statusColor}">${escapeHtml(debugState.status)}` +
      `${debugState.syncing ? ' (syncing)' : ''}</span>` +
      `</div>` +
      `<div style="opacity:.85;margin-bottom:8px;">` +
      `source: <b>${escapeHtml(debugState.source || '—')}</b> · attempt ${debugState.attempt}` +
      `${debugState.noSync ? ' · <b style="color:#f0c14b">instockNoSync</b>' : ''}` +
      `<br>legacy cart.attributes._instock: <b>${escapeHtml(debugState.legacyAttr ?? '∅')}</b>` +
      `<br>shipping note: <b>${escapeHtml(debugState.shippingNote || '—')}</b>` +
      `<br>cart.note: <b>${escapeHtml(debugState.cartNote || '—')}</b>` +
      (debugState.lastError
        ? `<br><span style="color:#ff6b6b">error: ${escapeHtml(debugState.lastError)}</span>`
        : '') +
      (debugState.updatedAt
        ? `<br><span style="opacity:.6">${escapeHtml(debugState.updatedAt)}</span>`
        : '') +
      `</div>` +
      (lineRows
        ? `<table style="width:100%;border-collapse:collapse;margin-bottom:8px;"><tbody>${lineRows}</tbody></table>`
        : `<div style="opacity:.7;margin-bottom:8px;">(no cart lines)</div>`) +
      `<div style="display:flex;gap:8px;flex-wrap:wrap;">` +
      `<button type="button" data-ii-instock-debug-refresh style="cursor:pointer;padding:4px 8px;border-radius:4px;border:1px solid #666;background:#222;color:#fff;">Refresh / re-sync</button>` +
      `<button type="button" data-ii-instock-debug-close style="cursor:pointer;padding:4px 8px;border-radius:4px;border:1px solid #666;background:#222;color:#fff;">Hide</button>` +
      `</div>` +
      `<div style="margin-top:8px;opacity:.55;font-size:11px;">` +
      `URL: ?ii_instock_debug=1 · alias ?instockDebug=1 · skip writes ?instockNoSync=1` +
      `</div>`;

    root.querySelector('[data-ii-instock-debug-refresh]')?.addEventListener(
      'click',
      () => {
        setDebugState({ status: 'refreshing', lastError: null });
        flushLineInstockProperties().catch((err) => {
          setDebugState({
            status: 'error',
            lastError: err instanceof Error ? err.message : String(err),
          });
        });
      },
    );
    root.querySelector('[data-ii-instock-debug-close]')?.addEventListener(
      'click',
      () => root.remove(),
    );
  }

  /**
   * @param {string} raw
   * @returns {string}
   */
  function escapeHtml(raw) {
    return String(raw)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');
  }

  /**
   * @param {string} reason
   */
  async function captureDebugAfterSync(reason) {
    if (!isDebugMode()) return;
    const routes = typeof window !== 'undefined' && window.routes;
    let cart = null;
    let serverLines = null;
    try {
      if (routes?.cart_url) cart = await fetchCart(routes.cart_url);
      serverLines = await fetchComputedLinesFromServer();
    } catch (err) {
      setDebugState({
        status: 'error',
        lastError: err instanceof Error ? err.message : String(err),
      });
      return;
    }
    refreshDebugSnapshot({
      cart,
      serverLines,
      domLines: getComputedLinesFromDom(),
      source: serverLines ? 'server' : 'dom',
      status: debugState.lastError ? 'error' : 'ok',
      attempt: debugState.attempt,
    });
    debugLog('debug snapshot', reason, debugState);
  }

  /**
   * Prefer `.cart-instock-data` inside cart items section, outside drawer when possible.
   * @returns {Element | null}
   */
  function getInstockDataElement() {
    const inItemsSections = document.querySelectorAll(
      `${ITEMS_SECTION_SELECTOR} ${DATA_SELECTOR}`,
    );
    if (inItemsSections.length) {
      for (let i = 0; i < inItemsSections.length; i++) {
        const node = inItemsSections[i];
        if (!node.closest(CART_DRAWER_SELECTOR)) {
          return node;
        }
      }
      return inItemsSections[0];
    }

    const nodes = document.querySelectorAll(DATA_SELECTOR);
    if (!nodes.length) return null;

    const isCartPage =
      typeof window !== 'undefined' &&
      window.location?.pathname?.replace(/\/$/, '') === '/cart';

    let fallback = null;
    for (let i = 0; i < nodes.length; i++) {
      const node = nodes[i];
      const inDrawer = node.closest(CART_DRAWER_SELECTOR);

      if (isCartPage) {
        if (inDrawer) {
          if (!fallback) fallback = node;
          continue;
        }
        return node;
      }

      if (inDrawer) return node;
      if (!fallback) fallback = node;
    }
    return fallback;
  }

  /**
   * @param {Element | null | undefined} el
   * @returns {InstockLine[] | null}
   */
  function parseInstockLines(el) {
    if (!el?.textContent) return null;
    try {
      const data = JSON.parse(el.textContent);
      const rows = data.lines;
      if (!Array.isArray(rows)) {
        debugLog('computed: missing lines', data);
        return null;
      }

      /** @type {InstockLine[]} */
      const out = [];
      for (let i = 0; i < rows.length; i++) {
        const row = rows[i];
        const key = row && typeof row.key === 'string' ? row.key : '';
        const variantId = Number(row?.variantId);
        const quantity = Number(row?.quantity);
        if (
          !key ||
          typeof row.instock !== 'boolean' ||
          !Number.isFinite(variantId) ||
          !Number.isFinite(quantity)
        ) {
          debugLog('computed: invalid line', i, row);
          return null;
        }
        out.push({ key, variantId, quantity, instock: row.instock });
      }
      debugLog('computed lines:', out);
      return out;
    } catch (err) {
      debugLog('computed: JSON parse failed', err);
      return null;
    }
  }

  /**
   * @returns {InstockLine[] | null}
   */
  function getComputedLinesFromDom() {
    const el = getInstockDataElement();
    if (!el) {
      debugLog('computed: missing element', DATA_SELECTOR);
      return null;
    }
    return parseInstockLines(el);
  }

  /**
   * Fresh section HTML. Cache-busted so a previous cart render cannot supply flags.
   * @returns {Promise<InstockLine[] | null>}
   */
  async function fetchComputedLinesFromServer() {
    const routes = typeof window !== 'undefined' && window.routes;
    if (!routes?.cart_url) return null;

    try {
      const url = new URL(routes.cart_url, window.location.origin);
      url.searchParams.set('section_id', CART_ITEMS_SECTION_ID);
      url.searchParams.set('instock_ts', String(Date.now()));

      const res = await fetch(url.toString(), {
        cache: 'no-store',
        headers: { 'X-Requested-With': 'XMLHttpRequest' },
      });
      if (!res.ok) {
        debugLog('computed(server): fetch failed', res.status);
        return null;
      }

      const htmlText = await res.text();
      const doc = new DOMParser().parseFromString(htmlText, 'text/html');
      const parsed = parseInstockLines(doc.querySelector(DATA_SELECTOR));
      if (parsed) debugLog('computed lines (server):', parsed);
      return parsed;
    } catch (err) {
      debugLog('computed(server): error', err);
      return null;
    }
  }

  /**
   * Canonical stored value for line `properties._instock`, or null when absent.
   * Missing must not be treated as false — otherwise we never POST `"false"` for OOS lines.
   * @param {unknown} raw
   * @returns {'true' | 'false' | null}
   */
  function instockPropStoredValue(raw) {
    if (raw === undefined || raw === null || raw === '') return null;
    const s = String(raw).trim().toLowerCase();
    if (s === 'true') return 'true';
    if (s === 'false') return 'false';
    return null;
  }

  /**
   * @param {Record<string, unknown> | null | undefined} properties
   * @returns {Record<string, string>}
   */
  function propertiesForRequest(properties) {
    const out = {};
    if (!properties || typeof properties !== 'object') return out;
    for (const key of Object.keys(properties)) {
      const v = properties[key];
      if (v == null) continue;
      out[key] = String(v);
    }
    return out;
  }

  /**
   * @param {string} cartUrl
   * @returns {Promise<{ items: Array<{ key: string, variant_id: number, quantity: number, properties?: Record<string, unknown> }>, attributes?: Record<string, string> } | null>}
   */
  async function fetchCart(cartUrl) {
    const res = await fetch(`${cartUrl}.js`, { cache: 'no-store' });
    if (!res.ok) {
      debugLog('sync: cart fetch failed', res.status);
      return null;
    }
    return res.json();
  }

  /**
   * Clear legacy cart-level _instock attribute if set.
   */
  async function clearLegacyCartAttribute(cart, fetchConfigFn, updateUrl) {
    if (!cart.attributes || !(LEGACY_CART_ATTR_KEY in cart.attributes)) return;

    const attrs = { ...cart.attributes, [LEGACY_CART_ATTR_KEY]: '' };
    const config = fetchConfigFn('json');
    config.body = JSON.stringify({ attributes: attrs });
    const res = await fetch(updateUrl, config);
    if (!res.ok) {
      debugLog('legacy cart attribute clear failed', res.status);
    } else {
      debugLog('cleared legacy cart.attributes', LEGACY_CART_ATTR_KEY);
    }
  }

  /**
   * @param {InstockLine[]} computed
   * @returns {Map<string, InstockLine> | null}
   */
  function indexLinesByKey(computed) {
    /** @type {Map<string, InstockLine>} */
    const byKey = new Map();
    for (let i = 0; i < computed.length; i++) {
      const line = computed[i];
      if (byKey.has(line.key)) {
        debugLog('sync: duplicate line key', line.key);
        return null;
      }
      byKey.set(line.key, line);
    }
    return byKey;
  }

  /**
   * Prefer cache-busted section HTML. Do not write from DOM when the server
   * snapshot is unavailable — drawer/page JSON can be stale after inventory
   * changes without a cart qty change (key/variant/qty still match).
   * @param {number} attempt
   * @returns {Promise<InstockLine[] | null>}
   */
  async function getComputedLinesForWrite(attempt) {
    const fromServer = await fetchComputedLinesFromServer();
    if (fromServer) return fromServer;

    if (attempt + 1 < MAX_SYNC_ATTEMPTS) {
      debugLog('computed: server unavailable; will retry (skip DOM write)');
      return null;
    }

    // Last attempt only: cart page DOM is usually same-request fresh.
    const isCartPage =
      typeof window !== 'undefined' &&
      window.location?.pathname?.replace(/\/$/, '') === '/cart';
    if (isCartPage) {
      const fromDom = getComputedLinesFromDom();
      if (fromDom) {
        debugLog('computed: last-attempt cart page DOM fallback');
        return fromDom;
      }
    }

    debugLog('computed: aborted (no fresh server snapshot)');
    return null;
  }

  /**
   * @param {number} attempt
   */
  async function syncOnce(attempt) {
    setDebugState({ attempt, syncing: true, status: 'syncing', lastError: null });
    const computed = await getComputedLinesForWrite(attempt);

    if (computed === null) {
      if (attempt + 1 < MAX_SYNC_ATTEMPTS) {
        debugLog('sync: retry after missing server snapshot');
        await syncOnce(attempt + 1);
      } else {
        debugLog('sync: aborted (no computed lines)');
        setDebugState({
          status: 'error',
          lastError: 'no computed lines (server snapshot unavailable)',
          source: null,
        });
      }
      return;
    }

    const routes = typeof window !== 'undefined' && window.routes;
    const fetchConfigFn = typeof fetchConfig === 'function' ? fetchConfig : null;
    if (!routes?.cart_url || !routes?.cart_change_url || !fetchConfigFn) {
      debugLog('sync: aborted (missing routes or fetchConfig)');
      setDebugState({
        status: 'error',
        lastError: 'missing routes or fetchConfig',
      });
      return;
    }

    if (isNoSyncMode()) {
      debugLog('sync: skipped (instockNoSync=1)');
      const cart = await fetchCart(routes.cart_url);
      refreshDebugSnapshot({
        cart,
        serverLines: computed,
        domLines: getComputedLinesFromDom(),
        source: 'server (no-sync)',
        status: 'skipped',
        attempt,
      });
      return;
    }

    const changeUrl = routes.cart_change_url.endsWith('.js')
      ? routes.cart_change_url
      : `${routes.cart_change_url}.js`;
    const updateUrl = routes.cart_update_url.endsWith('.js')
      ? routes.cart_update_url
      : `${routes.cart_update_url}.js`;

    let cart = await fetchCart(routes.cart_url);
    if (!cart) return;

    if (computed.length === 0 || cart.items.length === 0) {
      debugLog('sync: skipped (empty cart / no lines)');
      await clearLegacyCartAttribute(cart, fetchConfigFn, updateUrl);
      return;
    }

    const byKey = indexLinesByKey(computed);
    if (!byKey) return;

    let needsRetry = false;

    for (let i = 0; i < cart.items.length; i++) {
      const item = cart.items[i];
      const line = byKey.get(String(item.key));
      if (
        !line ||
        line.variantId !== Number(item.variant_id) ||
        line.quantity !== Number(item.quantity)
      ) {
        debugLog('line', item.key, 'skipped (snapshot does not match cart)');
        needsRetry = true;
        continue;
      }

      const expectedStr = line.instock ? 'true' : 'false';
      const storedStr = instockPropStoredValue(item.properties?.[PROP_KEY]);
      if (storedStr === expectedStr) {
        debugLog('line', item.key, PROP_KEY, 'ok', expectedStr);
        continue;
      }

      const latest = await fetchCart(routes.cart_url);
      if (!latest) {
        needsRetry = true;
        break;
      }
      cart = latest;
      const current = cart.items.find((entry) => String(entry.key) === line.key);
      if (
        !current ||
        Number(current.variant_id) !== line.variantId ||
        Number(current.quantity) !== line.quantity
      ) {
        debugLog('line', line.key, 'skipped (cart changed before write)');
        needsRetry = true;
        continue;
      }

      debugLog(
        'line',
        line.key,
        PROP_KEY,
        'stored:',
        storedStr === null ? '(missing)' : storedStr,
        '→',
        expectedStr,
      );

      const config = fetchConfigFn('json');
      config.body = JSON.stringify({
        id: current.key,
        quantity: current.quantity,
        properties: {
          ...propertiesForRequest(current.properties),
          [PROP_KEY]: expectedStr,
        },
      });

      const changeRes = await fetch(changeUrl, config);
      if (!changeRes.ok) {
        debugLog('sync: change failed line', line.key, changeRes.status);
        needsRetry = true;
        continue;
      }
      debugLog('sync: updated line', line.key, PROP_KEY, expectedStr);
    }

    if (needsRetry && attempt + 1 < MAX_SYNC_ATTEMPTS) {
      debugLog('sync: retry after stale snapshot or change failure');
      await syncOnce(attempt + 1);
      return;
    }

    const after = await fetchCart(routes.cart_url);
    if (after) await clearLegacyCartAttribute(after, fetchConfigFn, updateUrl);
  }

  async function ensureLineInstockProperties() {
    if (syncing) {
      syncQueued = true;
      return;
    }
    syncing = true;
    setDebugState({ syncing: true, status: 'syncing' });
    try {
      await syncOnce(0);
      if (isDebugMode()) await captureDebugAfterSync('auto');
    } catch (err) {
      debugLog('sync: error', err);
      setDebugState({
        status: 'error',
        lastError: err instanceof Error ? err.message : String(err),
      });
    } finally {
      syncing = false;
      setDebugState({ syncing: false });
      if (syncQueued) {
        syncQueued = false;
        scheduleSync();
      }
    }
  }

  /**
   * Flush any pending debounce and run sync to completion (checkout gate).
   * @returns {Promise<void>}
   */
  async function flushLineInstockProperties() {
    clearTimeout(debounceTimer);
    debounceTimer = null;
    while (syncing) {
      syncQueued = true;
      await new Promise((resolve) => setTimeout(resolve, 25));
    }
    await ensureLineInstockProperties();
  }

  function scheduleSync() {
    clearTimeout(debounceTimer);
    debounceTimer = setTimeout(ensureLineInstockProperties, DEBOUNCE_MS);
  }

  /**
   * Re-validate line `_instock` immediately before leaving for checkout.
   * Does not cover Shop Pay / accelerated checkout (no form submit).
   * @param {Event} event
   */
  function onCheckoutFormSubmit(event) {
    const form = event.target;
    if (!(form instanceof HTMLFormElement)) return;
    if (!form.matches(CHECKOUT_FORM_SELECTOR)) return;
    if (form.dataset.instockSyncReady === '1') {
      delete form.dataset.instockSyncReady;
      return;
    }
    if (isNoSyncMode()) return;

    event.preventDefault();
    // Keep the clicked button (name="checkout"); without it Shopify treats
    // the POST to /cart as a cart update and just reloads the page.
    const submitter = /** @type {SubmitEvent} */ (event).submitter || null;

    flushLineInstockProperties()
      .catch((err) => {
        debugLog('checkout sync: error', err);
      })
      .finally(() => {
        form.dataset.instockSyncReady = '1';
        if (typeof form.requestSubmit === 'function' && submitter) {
          form.requestSubmit(submitter);
        } else {
          if (!form.querySelector('input[type="hidden"][name="checkout"]')) {
            const input = document.createElement('input');
            input.type = 'hidden';
            input.name = 'checkout';
            input.value = '';
            form.appendChild(input);
          }
          form.submit();
        }
      });
  }

  function init() {
    if (isDebugMode()) {
      debugLog('debug mode on (overlay + console)');
      setDebugState({ status: 'boot', noSync: isNoSyncMode() });
      if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', renderDebugUi, { once: true });
      } else {
        renderDebugUi();
      }
    }

    if (document.readyState !== 'loading') {
      scheduleSync();
    } else {
      document.addEventListener('DOMContentLoaded', scheduleSync);
    }

    document.addEventListener('cart:added', scheduleSync);
    document.addEventListener('cart:update', scheduleSync);
    document.addEventListener('submit', onCheckoutFormSubmit, true);
  }

  init();
})();
})();
(function () {
/**
 * Debug logging function that only logs if 'debug_quick_atc=1' is present in URL query parameters
 * @param {...any} args - Arguments to log
 */
function debugLog() {
  // No-op: debug logging removed for production
}

/**
 * QuickATCTrigger Web Component
 *
 * Custom element that automatically initializes when connected to DOM.
 * Manages quick add-to-cart modal functionality with product content preloading
 * and dynamic section fetching.
 *
 * Features:
 * - Auto-initialization via connectedCallback
 * - Hover-based content preloading
 * - Modal state management
 * - Automatic cleanup on disconnect
 *
 * @example
 * // HTML markup (auto-initialized when added to DOM):
 * <quick-atc-trigger data-modal-id="quick-atc-modal-123"
 *                    data-product-handle="product-handle"
 *                    data-product-url="/products/handle?section_id=product--main">
 *   <button data-modal-trigger="quick-atc-modal-123">Quick Add</button>
 * </quick-atc-trigger>
 */
class QuickATCTrigger extends HTMLElement {
  static #instances = new Set();
  static #globalAbortController = null;
  static #variantRadiosPromise = null;

  #modalId = '';
  #productHandle = '';
  #productUrl = '';
  #isInitialized = false;
  #preloadedHTML = null;
  #isLoading = false;
  #abortController = null;
  #hoverHandler = null;

  /**
   * Web Component lifecycle - called when element is added to DOM
   */
  connectedCallback() {
    debugLog('🔌 QuickATCTrigger: Connected to DOM');
    QuickATCTrigger.#instances.add(this);
    QuickATCTrigger.#ensureGlobalListeners();
    this.#init();
  }

  /**
   * Web Component lifecycle - called when element is removed from DOM
   */
  disconnectedCallback() {
    debugLog('🔌 QuickATCTrigger: Disconnected from DOM');
    this.#cleanup();
    QuickATCTrigger.#instances.delete(this);
    QuickATCTrigger.#teardownGlobalListeners();
  }

  /**
   * Initialize the component
   * @private
   */
  #init() {
    if (this.#isInitialized) return;

    // Extract data from attributes
    this.#modalId = this.dataset.modalId || '';
    this.#productHandle = this.dataset.productHandle || '';
    this.#productUrl = this.dataset.productUrl || '';

    if (!this.#modalId || !this.#productHandle || !this.#productUrl) {
      return;
    }

    // Ensure product URL has section_id parameter for product--quick-atc section
    this.#productUrl = this.#ensureSectionId(this.#productUrl);

    debugLog('✅ QuickATCTrigger: Initializing', {
      modalId: this.#modalId,
      productHandle: this.#productHandle,
      productUrl: this.#productUrl
    });

    try {
      this.#addHoverListeners();
      this.#isInitialized = true;
    } catch (error) {
      this.#handleError('Failed to initialize QuickATCTrigger', error);
    }
  }

  /**
   * Ensure the URL has the section_id parameter for product--quick-atc
   * @param {string} url - The product URL
   * @returns {string} URL with section_id parameter
   * @private
   */
  #ensureSectionId(url) {
    try {
      // Parse the URL
      const urlObj = new URL(url, window.location.origin);

      // Check if section_id is already present
      if (!urlObj.searchParams.has('section_id')) {
        // Add section_id=product--quick-atc parameter
        urlObj.searchParams.set('section_id', 'product--quick-atc');
      }

      // Return the pathname + search params (relative URL)
      return urlObj.pathname + urlObj.search;
    } catch (error) {
      // Fallback: append section_id if not present
      const separator = url.includes('?') ? '&' : '?';
      return url.includes('section_id') ? url : `${url}${separator}section_id=product--quick-atc`;
    }
  }

  /**
   * Add hover listener for content preloading
   * @private
   */
  #addHoverListeners() {
    // Find parent product card or use self as hover target
    const hoverTarget = this.closest('.product-card') || this;

    this.#hoverHandler = () => {
      if (!this.#preloadedHTML && !this.#isLoading) {
        this.#preloadContent();
      }
    };

    hoverTarget.addEventListener('mouseenter', this.#hoverHandler, { once: true, passive: true });
  }

  /**
   * Preload product content for faster modal display
   * @private
   */
  async #preloadContent() {
    if (this.#isLoading || this.#preloadedHTML) return;

    debugLog('🔄 QuickATCTrigger: Preloading content', { modalId: this.#modalId });
    this.#isLoading = true;

    try {
      this.#abortController = new AbortController();

      const response = await fetch(this.#productUrl, {
        signal: this.#abortController.signal,
        headers: {
          'Accept': 'text/html',
          'X-Requested-With': 'XMLHttpRequest'
        }
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch: ${response.status} ${response.statusText}`);
      }

      const html = await response.text();
      this.#preloadedHTML = html;
      this.#isLoading = false;

      debugLog('✅ QuickATCTrigger: Preload successful', { modalId: this.#modalId });

      // Dispatch preload success event
      this.dispatchEvent(new CustomEvent('quick-atc:preload-success', {
        detail: { productHandle: this.#productHandle, modalId: this.#modalId },
        bubbles: true
      }));

    } catch (error) {
      this.#isLoading = false;
      if (error.name !== 'AbortError') {
        this.#preloadedHTML = null;
      }
    }
  }

  /**
   * Handle modal open events
   * @private
   */
  async #handleModalOpen(event) {
    const { modalId } = event.detail || {};
    if (modalId !== this.#modalId) return;

    debugLog('🚀 QuickATCTrigger: Handling modal open', { modalId: this.#modalId });

    // Load variant-radios first so it's defined before any fetched content (or section-fetcher) injects
    try {
      await this.#ensureVariantRadiosLoaded();
    } catch (e) {
      debugLog('⚠️ QuickATCTrigger: variant-radios load failed', e);
    }

    // Show loading state when modal opens
    this.#setLoadingState(true);

    try {
      // Wait for modal DOM
      await new Promise(resolve => setTimeout(resolve, 50));

      const modalElements = this.#getModalElements();
      if (!modalElements) {
        this.#setLoadingState(false);
        return;
      }

      const { loadingEl, errorEl, productEl } = modalElements;

      if (this.#preloadedHTML) {
        debugLog('✅ QuickATCTrigger: Using preloaded content');
        await this.#ensureVariantRadiosLoaded();
        this.#displayContent(productEl, this.#preloadedHTML);
        this.#setLoadingState(false);
      } else {
        debugLog('🔄 QuickATCTrigger: Fetching content...');
        // Don't hide content if we don't have loading elements
        if (loadingEl) {
          this.#showState(loadingEl);
        }
        await this.#fetchAndDisplayContent(productEl, errorEl);
        this.#setLoadingState(false);
      }
    } catch (error) {
      this.#handleError('Failed to handle modal open', error);
      this.#setLoadingState(false);
    }
  }

  /**
   * Handle modal close events
   * @private
   */
  #handleModalClose(event) {
    const { modalId } = event.detail || {};
    if (modalId === this.#modalId) {
      if (this.#abortController) {
        this.#abortController.abort();
        this.#abortController = null;
      }
      // Reset loading state when modal closes
      this.#setLoadingState(false);
    }
  }

  /**
   * Close the modal if it is the quick ATC modal (so cart drawer can open without conflict).
   * Used on cart:adding (immediate close) and cart:added (legacy).
   * @private
   */
  #closeModalIfQuickATC() {
    if (!window.electricModal) return;
    const modal = document.getElementById('electric-modal-container');
    if (!modal || modal.classList.contains('hidden')) return;
    const modalContent = modal.querySelector('.electric-modal-content');
    const hasQuickATCContent = modalContent?.querySelector('.quick-atc-product-content, section-fetcher');
    if (!hasQuickATCContent) return;
    window.electricModal.close('cart-adding');
  }

  /**
   * Handle cart add events - close the modal when item is added to cart
   * @param {CustomEvent} event - Cart added event
   * @private
   */
  #handleCartAdd(event) {
    const modal = document.getElementById('electric-modal-container');
    if (!modal || modal.classList.contains('hidden')) return;
    const modalContent = modal.querySelector('.electric-modal-content');
    const hasQuickATCContent = modalContent?.querySelector('.quick-atc-product-content, section-fetcher');
    if (!hasQuickATCContent) return;
    debugLog('🛒 QuickATCTrigger: Item added to cart, closing modal', {
      modalId: this.#modalId,
      eventSource: event.detail?.source
    });
    window.electricModal.close('cart-added');
  }

  /**
   * Set loading state for the trigger button
   * @param {boolean} isLoading - Whether to show loading state
   * @private
   */
  #setLoadingState(isLoading) {
    const triggerButton = this.querySelector('[data-quick-atc-trigger]') ||
                         document.querySelector(`[data-quick-atc-trigger="${this.#modalId}"]`);
    if (!triggerButton) return;

    const iconElement = triggerButton.querySelector('[data-icon]');
    const spinnerElement = triggerButton.querySelector('[data-spinner]');

    if (isLoading) {
      triggerButton.disabled = true;
      triggerButton.setAttribute('aria-label', 'Loading product...');
      if (iconElement) iconElement.classList.add('opacity-0');
      if (spinnerElement) spinnerElement.classList.remove('hidden');
    } else {
      triggerButton.disabled = false;
      const productTitle = this.#productHandle || 'product';
      triggerButton.setAttribute('aria-label', `Quick add ${productTitle} to cart`);
      if (iconElement) iconElement.classList.remove('opacity-0');
      if (spinnerElement) spinnerElement.classList.add('hidden');
    }
  }

  /**
   * Get modal DOM elements
   * @private
   */
  #getModalElements() {
    const modal = document.getElementById('electric-modal-container');
    if (!modal) return null;

    const modalContent = modal.querySelector('.electric-modal-content');
    if (!modalContent) return null;

    // Look for the section-fetcher element which contains the actual content
    const sectionFetcher = modalContent.querySelector('section-fetcher');

    return {
      loadingEl: modalContent.querySelector('.quick-atc-loading'),
      errorEl: modalContent.querySelector('.quick-atc-error'),
      productEl: sectionFetcher || modalContent.querySelector('.quick-atc-product-content')
    };
  }

  /**
   * Ensure variant-radios custom element is defined (for fetched product--quick-atc HTML).
   * Loads product--variant-radios.js on demand when not on product page.
   * @returns {Promise<void>}
   * @private
   */
  async #ensureVariantRadiosLoaded() {
    if (customElements.get('variant-radios')) return;
    if (QuickATCTrigger.#variantRadiosPromise) return QuickATCTrigger.#variantRadiosPromise;
    let url = window.theme?.assetsVariantRadios;
    if (!url) {
      const deferred = document.querySelector('script[src*="deferred.js"]');
      if (deferred?.src) {
        url = deferred.src.replace(/deferred\.js(\?.*)?$/i, 'product--variant-radios.js$1');
      } else {
        url = `${window.location.origin}/assets/product--variant-radios.js`;
      }
      debugLog('⚠️ QuickATCTrigger: using fallback URL for variant-radios', url);
    }
    QuickATCTrigger.#variantRadiosPromise = new Promise((resolve, reject) => {
      const script = document.createElement('script');
      script.src = url;
      script.type = 'module';
      script.onload = () => resolve();
      script.onerror = () => reject(new Error('Failed to load variant-radios'));
      document.head.appendChild(script);
    });
    return QuickATCTrigger.#variantRadiosPromise;
  }

  /**
   * Fetch and display product content
   * @private
   */
  async #fetchAndDisplayContent(productEl, errorEl) {
    try {
      await this.#ensureVariantRadiosLoaded();

      this.#abortController = new AbortController();

      const response = await fetch(this.#productUrl, {
        signal: this.#abortController.signal,
        headers: {
          'Accept': 'text/html',
          'X-Requested-With': 'XMLHttpRequest'
        }
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch: ${response.status} ${response.statusText}`);
      }

      const html = await response.text();
      this.#preloadedHTML = html;
      this.#displayContent(productEl, html);

    } catch (error) {
      if (error.name !== 'AbortError') {
        this.#showState(errorEl);
      }
    }
  }

  /**
   * Display product content in modal
   * @private
   */
  #displayContent(productEl, html) {
    if (!productEl) return;

    try {
      productEl.innerHTML = html;

      // Reinitialize Shopify scripts
      if (window.Shopify?.PaymentButton?.init) {
        window.Shopify.PaymentButton.init();
      }
      if (window.ProductForm?.init) {
        window.ProductForm.init();
      }

      // Dispatch content loaded event
      this.dispatchEvent(new CustomEvent('quick-atc:content-loaded', {
        detail: { productHandle: this.#productHandle, modalId: this.#modalId },
        bubbles: true
      }));

      // Only show state if we have loading/error elements to manage
      const modal = document.getElementById('electric-modal-container');
      if (modal?.querySelector('.quick-atc-loading')) {
        this.#showState(productEl);
      }

    } catch (error) {
      // Display content failed
    }
  }

  /**
   * Show specific modal state
   * @private
   */
  #showState(elementToShow) {
    const modalElements = this.#getModalElements();
    if (!modalElements) return;

    const { loadingEl, errorEl, productEl } = modalElements;

    // Hide all states
    [loadingEl, errorEl, productEl].forEach(el => {
      if (el) el.classList.add('hidden');
    });

    // Show specified state
    if (elementToShow) {
      elementToShow.classList.remove('hidden');
    }
  }

  /**
   * Handle errors
   * @private
   */
  #handleError(message, error) {
    this.dispatchEvent(new CustomEvent('quick-atc:error', {
      detail: {
        message,
        error: error.message,
        modalId: this.#modalId,
        productHandle: this.#productHandle
      },
      bubbles: true
    }));
  }

  /**
   * Cleanup resources
   * @private
   */
  #cleanup() {
    // Abort any ongoing requests
    if (this.#abortController) {
      this.#abortController.abort();
      this.#abortController = null;
    }

    // Remove hover listener
    if (this.#hoverHandler) {
      const hoverTarget = this.closest('.product-card') || this;
      hoverTarget.removeEventListener('mouseenter', this.#hoverHandler);
    }

    // Clear state
    this.#isInitialized = false;
    this.#preloadedHTML = null;
  }

  static #ensureGlobalListeners() {
    if (QuickATCTrigger.#globalAbortController) return;
    QuickATCTrigger.#globalAbortController = new AbortController();
    const signal = QuickATCTrigger.#globalAbortController.signal;

    document.addEventListener('modal:open', (event) => {
      for (const instance of QuickATCTrigger.#instances) {
        instance.#handleModalOpen(event);
      }
    }, { passive: true, signal });

    document.addEventListener('modal:close', (event) => {
      for (const instance of QuickATCTrigger.#instances) {
        instance.#handleModalClose(event);
      }
    }, { passive: true, signal });

    document.addEventListener('cart:adding', () => {
      for (const instance of QuickATCTrigger.#instances) {
        instance.#closeModalIfQuickATC();
      }
    }, { passive: true, signal });

    document.addEventListener('cart:added', (event) => {
      for (const instance of QuickATCTrigger.#instances) {
        instance.#handleCartAdd(event);
      }
    }, { passive: true, signal });
  }

  static #teardownGlobalListeners() {
    if (QuickATCTrigger.#instances.size > 0) return;
    QuickATCTrigger.#globalAbortController?.abort();
    QuickATCTrigger.#globalAbortController = null;
  }
}

// Register the custom element
customElements.define('quick-atc-trigger', QuickATCTrigger);

/**
 * Legacy support - convert existing buttons to web components
 * This observer watches for dynamically added elements with data-quick-atc-trigger
 * and wraps them in the custom element
 */
class QuickATCLegacyAdapter {
  #observer = null;
  #processedElements = new WeakSet();

  constructor() {
    this.#init();
  }

  #init() {
    // Process existing elements
    this.#processExistingElements();

    // Setup mutation observer for dynamic content
    this.#setupObserver();
  }

  #processExistingElements() {
    const triggers = document.querySelectorAll('[data-quick-atc-trigger]:not(quick-atc-trigger [data-quick-atc-trigger])');
    triggers.forEach(trigger => this.#wrapTrigger(trigger));
  }

  #wrapTrigger(trigger) {
    // Skip if already processed
    if (this.#processedElements.has(trigger)) return;
    if (trigger.closest('quick-atc-trigger')) return;

    const modalId = trigger.dataset.quickAtcTrigger;
    const productHandle = trigger.dataset.modalProductHandle;
    let productUrl = trigger.dataset.modalProductUrl;

    if (!modalId || !productHandle || !productUrl) return;

    debugLog('🔄 QuickATCLegacyAdapter: Converting legacy trigger', { modalId, productUrl });

    // Create web component wrapper
    const wrapper = document.createElement('quick-atc-trigger');
    wrapper.dataset.modalId = modalId;
    wrapper.dataset.productHandle = productHandle;
    wrapper.dataset.productUrl = productUrl;

    // Wrap the trigger element
    trigger.parentNode.insertBefore(wrapper, trigger);
    wrapper.appendChild(trigger);

    this.#processedElements.add(trigger);
  }

  #setupObserver() {
    this.#observer = new MutationObserver(mutations => {
      for (const mutation of mutations) {
        if (mutation.type === 'childList') {
          mutation.addedNodes.forEach(node => {
            if (node.nodeType === Node.ELEMENT_NODE) {
              // Check if the node itself is a trigger
              if (node.matches?.('[data-quick-atc-trigger]:not(quick-atc-trigger [data-quick-atc-trigger])')) {
                this.#wrapTrigger(node);
              }

              // Check for triggers within the node
              const triggers = node.querySelectorAll?.('[data-quick-atc-trigger]:not(quick-atc-trigger [data-quick-atc-trigger])');
              triggers?.forEach(trigger => this.#wrapTrigger(trigger));
            }
          });
        }
      }
    });

    this.#observer.observe(document.body, {
      childList: true,
      subtree: true
    });
  }

  destroy() {
    if (this.#observer) {
      this.#observer.disconnect();
      this.#observer = null;
    }
  }
}

// Initialize legacy adapter when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    window.quickATCLegacyAdapter = new QuickATCLegacyAdapter();
  });
} else {
  window.quickATCLegacyAdapter = new QuickATCLegacyAdapter();
}

// Global handler for quick-atc button clicks to show spinner
document.addEventListener('click', (event) => {
  const button = event.target.closest('[data-quick-atc-trigger]');
  if (button) {
    const iconElement = button.querySelector('[data-icon]');
    const spinnerElement = button.querySelector('[data-spinner]');

    if (iconElement && spinnerElement) {
      iconElement.classList.add('opacity-0');
      spinnerElement.classList.remove('hidden');
    }
  }
}, true);

// Export for module systems
if (typeof module !== 'undefined' && module.exports) {
  module.exports = { QuickATCTrigger, QuickATCLegacyAdapter };
}
})();
(function () {
/**
 * Klaviyo Added to Cart v3 + legacy Added to Cart tracking.
 * Loaded from deferred.js so head scripts stay lean.
 */

/** @type {Record<string, object>} */
const productDataCache = { ...(window.theme?.klaviyoCart?.productCache || {}) };
const legacyProductContext = window.theme?.klaviyoCart?.legacyProductContext || null;
const shopName = window.theme?.klaviyoCart?.shopName || '';

/**
 * @param {object} item
 * @param {object|null} productData
 * @returns {object}
 */
function formatCartItem(item, productData) {
  const imageUrl = item.image
    ? (item.image.startsWith('http') ? item.image : `https:${item.image}`)
    : '';
  const price = typeof item.price === 'number' ? item.price / 100 : parseFloat(item.price) || 0;
  const compareAtPrice = item.original_price && item.original_price !== item.price
    ? (typeof item.original_price === 'number' ? item.original_price / 100 : parseFloat(item.original_price) || 0)
    : null;

  return {
    ProductID: item.product_id.toString(),
    SKU: item.sku || '',
    ProductName: item.product_title || item.title || '',
    ProductTitle: item.title || '',
    Quantity: item.quantity || 1,
    ItemPrice: price,
    RowTotal: price * (item.quantity || 1),
    ProductURL: item.url || '',
    ImageURL: imageUrl,
    CompareAtPrice: compareAtPrice,
    VariantID: item.variant_id ? item.variant_id.toString() : '',
    VariantTitle: item.variant_title || '',
    Vendor: shopName,
    ReviewName: productData?.reviewName || '',
    DisplayReview: productData?.displayReview || '',
    ReviewRating: productData?.reviewRating ?? null,
    ReviewCount: productData?.reviewCount ?? null,
  };
}

/**
 * @param {object} item
 * @returns {object}
 */
function formatLegacyItemFromCartItem(item) {
  const imageUrl = item.image
    ? (item.image.startsWith('http') ? item.image : `https:${item.image}`)
    : '';
  const price = typeof item.price === 'number' ? item.price / 100 : parseFloat(item.price) || 0;
  const compareAtPrice = item.original_price && item.original_price !== item.price
    ? (typeof item.original_price === 'number' ? item.original_price / 100 : parseFloat(item.original_price) || 0)
    : null;

  return {
    Name: item.product_title || item.title || '',
    ProductID: item.product_id ? item.product_id.toString() : '',
    Categories: [],
    ImageURL: imageUrl,
    URL: item.url || '',
    Brand: shopName,
    Price: price,
    CompareAtPrice: compareAtPrice,
  };
}

/**
 * @returns {object|undefined}
 */
function getLearnq() {
  if (typeof window._learnq !== 'undefined' && window._learnq.push) {
    return window._learnq;
  }
  return undefined;
}

/**
 * @param {object} eventDetail
 */
function trackLegacyAddedToCart(eventDetail) {
  try {
    const learnqInstance = getLearnq();
    if (!learnqInstance) return;

    const detailProduct = eventDetail?.product;
    let legacyItem = null;

    if (detailProduct && typeof detailProduct === 'object') {
      legacyItem = formatLegacyItemFromCartItem(detailProduct);
    } else if (eventDetail?.cartItems?.length) {
      legacyItem = formatLegacyItemFromCartItem(eventDetail.cartItems[0]);
    } else if (legacyProductContext) {
      legacyItem = legacyProductContext;
    }

    if (!legacyItem) return;
    learnqInstance.push(['track', 'Added to Cart', legacyItem]);
  } catch {
    // Klaviyo optional
  }
}

/**
 * @param {object} eventDetail
 */
function trackAddedToCart(eventDetail) {
  try {
    const cart = eventDetail?.cart;
    const cartItems = eventDetail?.cartItems;

    if (!cart || !cartItems || cartItems.length === 0) {
      const cartUrl = window.routes?.cart_url;
      if (!cartUrl) return;
      fetch(`${cartUrl}.js`)
        .then((cartResponse) => cartResponse.json())
        .then((fetchedCart) => {
          trackAddedToCart({ cart: fetchedCart, cartItems: fetchedCart.items || [] });
        })
        .catch(() => {});
      return;
    }

    const items = cartItems.map((item) => {
      const productId = item.product_id;
      const productData = productDataCache[productId] || productDataCache[String(productId)] || null;
      return formatCartItem(item, productData);
    });

    const eventData = {
      Items: items,
      CartTotal: cart.total_price ? cart.total_price / 100 : 0,
      ItemCount: cart.item_count || 0,
    };

    const learnqInstance = getLearnq();
    if (learnqInstance) {
      learnqInstance.push(['track', 'Added to Cart v3', eventData]);
    }

    trackLegacyAddedToCart(eventDetail);
  } catch {
    // Klaviyo optional
  }
}

/**
 * @param {() => void} callback
 * @param {number} [maxAttempts=10]
 */
function waitForKlaviyo(callback, maxAttempts = 10) {
  let attempts = 0;
  const checkKlaviyo = () => {
    attempts += 1;
    if (
      typeof window._learnq !== 'undefined'
      && (window._learnq._loaded === true || typeof window._learnq.track === 'function')
    ) {
      callback();
    } else if (attempts < maxAttempts) {
      setTimeout(checkKlaviyo, 100);
    } else {
      callback();
    }
  };
  checkKlaviyo();
}

function initCartPageReplay() {
  if (window.theme?.templateName !== 'cart') return;
  if (!sessionStorage.getItem('cart:justAdded')) return;
  sessionStorage.removeItem('cart:justAdded');
  const cartUrl = window.routes?.cart_url;
  if (!cartUrl) return;

  fetch(`${cartUrl}.js`)
    .then((r) => r.json())
    .then((cart) => {
      document.dispatchEvent(new CustomEvent('cart:added', {
        bubbles: true,
        detail: { cart, cartItems: cart.items || [] },
      }));
    })
    .catch(() => {});
}

document.addEventListener('cart:added', (event) => {
  waitForKlaviyo(() => {
    trackAddedToCart(event.detail || {});
  });
});

initCartPageReplay();
})();
(function () {
/**
 * Load Hotjar after idle or first user interaction to avoid blocking main thread.
 */


const HOTJAR_ID = 1479522;
const HOTJAR_VERSION = 6;

function loadHotjar() {
  if (window.__hotjarLoaded) return;
  window.__hotjarLoaded = true;

  try {
    window.hj = window.hj || function hotjarQueue(...args) {
      (window.hj.q = window.hj.q || []).push(args);
    };
    window._hjSettings = { hjid: HOTJAR_ID, hjsv: HOTJAR_VERSION };
    const script = document.createElement('script');
    script.async = true;
    script.src = `https://static.hotjar.com/c/hotjar-${HOTJAR_ID}.js?sv=${HOTJAR_VERSION}`;
    document.head.appendChild(script);
  } catch {
    // Hotjar optional
  }
}

let scheduled = false;

function scheduleHotjar() {
  if (scheduled) return;
  scheduled = true;
  scheduleIdle(loadHotjar, { timeout: 8000 });
}

scheduleHotjar();

for (const eventName of ['scroll', 'click', 'touchstart', 'keydown']) {
  window.addEventListener(eventName, scheduleHotjar, { once: true, passive: true });
}
})();
/**
 * Deferred bundle: non-critical scripts loaded at end of body to reduce main thread work.
 * Runs after the critical bundle (index.js). Depends on window.theme, window.routes, etc. from critical.
 */

// Product card and cart UI (deferred to reduce main bundle)








const INTEGRATIONS_BUNDLE_NAME = 'deferred-integrations.js';
let integrationsBundlePromise = null;

function shouldLoadIntegrationsBundle() {
  return Boolean(
    document.querySelector(
      'electric-wish, electric-wish-counter, promo-banner, electric-rebuy-simplify, footer-newsletter, [data-gorgias-widget], .js-promo-banner-text',
    ),
  );
}

function loadDeferredIntegrationsBundle() {
  if (integrationsBundlePromise) return integrationsBundlePromise;

  integrationsBundlePromise = new Promise((resolve, reject) => {
    // Prefer the Liquid-resolved URL (carries the `?v=` content hash so the CDN
    // and browser pick up new builds). Fall back to resolving the name relative
    // to deferred.js when the global isn't present.
    let integrationsUrl = (window.theme && window.theme.integrationsBundleUrl) || '';
    if (!integrationsUrl) {
      const currentScript = document.currentScript
        || document.querySelector('script[src*="deferred.js"]');
      const baseUrl = currentScript?.src ? new URL(currentScript.src) : new URL(window.location.href);
      integrationsUrl = new URL(INTEGRATIONS_BUNDLE_NAME, baseUrl).toString();
    }

    const script = document.createElement('script');
    script.src = integrationsUrl;
    script.type = 'module';
    script.onload = () => resolve();
    script.onerror = () => reject(new Error(`Failed to load ${INTEGRATIONS_BUNDLE_NAME}`));
    document.head.appendChild(script);
  });

  return integrationsBundlePromise;
}

function scheduleDeferredIntegrationsLoad() {
  if (!shouldLoadIntegrationsBundle()) return;
  scheduleIdle(() => {
    loadDeferredIntegrationsBundle().catch(() => {});
  }, { timeout: 3000 });
}

// The wishlist counter/buttons self-initialize on custom-element upgrade once
// the integrations bundle (and WishlistStore) load.
document.addEventListener('DOMContentLoaded', () => {
  scheduleDeferredIntegrationsLoad();
});

if (document.readyState !== 'loading') {
  scheduleDeferredIntegrationsLoad();
}

// --- Chat button visibility (moved from main.js) ---
class ChatButtonVisibilityManager {
  #openDrawers = new Set();
  #chatButton = null;
  #abortController = null;

  constructor() {
    this.#initialize();
  }

  #initialize() {
    this.#abortController = new AbortController();
    const signal = this.#abortController.signal;

    const init = () => {
      scheduleIdle(() => {
        this.#chatButton = document.getElementById('chat-button');
        if (!this.#chatButton) {
          setTimeout(() => {
            this.#chatButton = document.getElementById('chat-button');
          }, 1000);
        }
        document.addEventListener('drawer:opened', (event) => this.#handleDrawerOpened(event), { signal });
        document.addEventListener('drawer:closed', (event) => this.#handleDrawerClosed(event), { signal });
        document.addEventListener('menu:opened', (event) => this.#handleDrawerOpened(event), { signal });
        document.addEventListener('menu:closed', (event) => this.#handleDrawerClosed(event), { signal });
      });
    };

    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', init);
    } else {
      init();
    }
  }

  #handleDrawerOpened(event) {
    const drawerId = event.detail?.drawer?.id || event.target?.id || 'drawer-' + Date.now();
    this.#openDrawers.add(drawerId);
    this.#updateVisibility();
  }

  #handleDrawerClosed(event) {
    const drawerId = event.detail?.drawer?.id || event.target?.id || 'drawer-' + Date.now();
    this.#openDrawers.delete(drawerId);
    this.#updateVisibility();
  }

  #updateVisibility() {
    const hidden = this.#openDrawers.size > 0;
    const display = hidden ? 'none' : '';
    if (!this.#chatButton) {
      this.#chatButton = document.getElementById('chat-button');
    }
    if (this.#chatButton) {
      this.#chatButton.style.display = display;
    }
    const gorgiasContainer = document.getElementById('gorgias-chat-container');
    if (gorgiasContainer) {
      gorgiasContainer.style.display = display;
    }
  }

  destroy() {
    this.#abortController?.abort();
    this.#openDrawers.clear();
    this.#chatButton = null;
  }
}

const chatButtonVisibilityManager = new ChatButtonVisibilityManager();
